import React, { useState, useEffect } from "react";
import {
BarChart,
Bar,
XAxis,
YAxis,
Tooltip,
CartesianGrid,
PieChart,
Pie,
Cell,
ResponsiveContainer,
Legend
} from "recharts";

const COLORS = ["#58735A","#3F1907","#A67C52","#C4A484","#D9C3A5"];

const DashboardPage = () => {

const [sales,setSales]=useState([]);
const [recipes,setRecipes]=useState([]);
const [inventory,setInventory]=useState([]);

const [filteredSales,setFilteredSales]=useState([]);

const [startDate,setStartDate]=useState("");
const [endDate,setEndDate]=useState("");

const [viewMode,setViewMode]=useState("charts");

const [metrics,setMetrics]=useState({
salesChart:[],
paymentMethods:[],
cmvMargin:[],
topProducts:[],
lowStock:[],
kpis:{
totalSales:0,
ticketAverage:0,
cmv:0,
margin:0,
itemsSold:0
}
});


// =====================
// NORMALIZE PAYMENT METHOD
// =====================

const normalizePaymentMethod = (method)=>{

if(!method) return "Efectivo";

const m = method.toLowerCase().trim();

if(m==="efectivo") return "Efectivo";
if(m==="tarjeta") return "Tarjeta";
if(m==="transferencia") return "Transferencia";

return "Efectivo";

};


// =====================
// LOAD SALES
// =====================

const loadSales = async () =>{

try{

const res = await fetch("/api/list_sales.php");
const data = await res.json();

if(data.success){

setSales(data.data || []);

}

}catch(err){

console.error("sales error",err);

}

};


// =====================
// LOAD RECIPES
// =====================

const loadRecipes = async () =>{

try{

const res = await fetch("/api/list_recipes.php");
const data = await res.json();

if(data.success){

setRecipes(data.data || []);

}

}catch(err){

console.error("recipes error",err);

}

};


// =====================
// LOAD INVENTORY
// =====================

const loadInventory = async () =>{

try{

const res = await fetch("/api/list_inventory.php");
const data = await res.json();

if(data.success){

setInventory(data.data || []);

}

}catch(err){

console.error("inventory error",err);

}

};


// =====================
// INITIAL LOAD
// =====================

useEffect(()=>{

loadSales();
loadRecipes();
loadInventory();

},[]);


// =====================
// DATE FILTER
// =====================

useEffect(()=>{

let filtered=[...sales];

if(startDate){

const start = new Date(startDate);

filtered = filtered.filter(s=>
new Date(s.sale_date) >= start
);

}

if(endDate){

const end = new Date(endDate);

filtered = filtered.filter(s=>
new Date(s.sale_date) <= end
);

}

setFilteredSales(filtered);

},[sales,startDate,endDate]);


// =====================
// CALCULATE METRICS
// =====================

useEffect(()=>{

// =================
// SALES CHART
// =================

const salesMap={};

filteredSales.forEach(s=>{

const date=s.sale_date.split(" ")[0];

const amount=
(Number(s.price)||0)*
(Number(s.quantitySold)||0);

salesMap[date]=(salesMap[date]||0)+amount;

});

const salesChart=Object.entries(salesMap).map(
([date,total])=>({date,total})
);


// =================
// PAYMENT METHODS
// =================

const paymentMap={};

filteredSales.forEach(s=>{

const method = normalizePaymentMethod(s.payment_method);

const amount=
(Number(s.price)||0)*
(Number(s.quantitySold)||0);

paymentMap[method]=(paymentMap[method]||0)+amount;

});

const paymentMethods=Object.entries(paymentMap).map(
([name,value])=>({name,value})
);


// =================
// CMV (🔥 FIX REAL)
// =================

// TOTAL SIN IVA
const totalSalesWithoutVAT = filteredSales.reduce((sum, s) => {

const qty = Number(s.quantitySold || s.quantity_sold || 0);
const price = Number(s.priceWithoutVAT || 0);

return sum + (price * qty);

}, 0);


// CMV REAL
const totalCMV = filteredSales.reduce((sum, sale) => {

const qty = Number(sale.quantitySold || sale.quantity_sold || 0);

// 🔥 BUSCAR RECETA
let recipe = recipes.find(r =>
Number(r.productId) === Number(sale.product_id)
);

if (!recipe) {
recipe = recipes.find(r =>
r.name?.toLowerCase().includes(sale.name?.toLowerCase())
);
}

// 🚨 SIN RECETA
if (!recipe) {
console.warn("❌ SIN RECETA:", sale.name);
return sum;
}

// 🔥 COSTO REAL POR UNIDAD
const costPerUnit =
Number(recipe.costPerServingCalculated || 0);

return sum + (costPerUnit * qty);

}, 0);


// RESULTADO
const cmvMargin = [
{ name: "CMV", value: totalCMV },
{ name: "Margen", value: totalSalesWithoutVAT - totalCMV }
];


// =================
// TOP PRODUCTS
// =================

const productMap={};

filteredSales.forEach(s=>{

productMap[s.name]=(productMap[s.name]||0)+
Number(s.quantitySold||0);

});

const topProducts = Object.entries(productMap)
.sort(([,a],[,b])=>b-a)
.slice(0,5)
.map(([name,quantity])=>({name,quantity}));


// =================
// LOW STOCK
// =================

const lowStock = inventory.filter(
i=>Number(i.current_quantity)<Number(i.low_stock_threshold||10)
);


// =================
// KPIs
// =================

const totalSales = filteredSales.reduce(
(sum,s)=>
sum+
(Number(s.price)||0)*
(Number(s.quantitySold)||0),
0
);

const totalItems = filteredSales.reduce(
(sum,s)=>sum+Number(s.quantitySold||0),
0
);

const ticketAverage =
filteredSales.length>0
? totalSales/filteredSales.length
:0;

const margin = totalSalesWithoutVAT-totalCMV;


setMetrics({
salesChart,
paymentMethods,
cmvMargin,
topProducts,
lowStock,
kpis:{
totalSales,
ticketAverage,
cmv:totalCMV,
margin,
itemsSold:totalItems
}
});

},[filteredSales,recipes,inventory]);


// =====================
// UI
// =====================

return(

<div className="p-6 bg-[#F9EFD7] min-h-screen">

<h1 className="text-3xl font-bold text-[#3F1907] mb-6">
Dashboard POS
</h1>


<div className="flex gap-3 mb-6">

<button
onClick={()=>setViewMode("charts")}
className={`px-4 py-2 rounded ${
viewMode==="charts"
?"bg-[#58735A] text-white"
:"bg-gray-200"
}`}
>
Gráficas
</button>

<button
onClick={()=>setViewMode("data")}
className={`px-4 py-2 rounded ${
viewMode==="data"
?"bg-[#58735A] text-white"
:"bg-gray-200"
}`}
>
Datos
</button>

</div>


<div className="flex gap-4 mb-6">

<input
type="date"
value={startDate}
onChange={e=>setStartDate(e.target.value)}
className="border p-2 rounded"
/>

<input
type="date"
value={endDate}
onChange={e=>setEndDate(e.target.value)}
className="border p-2 rounded"
/>

</div>


<div className="grid md:grid-cols-5 gap-4 mb-6">

<div className="bg-white p-4 rounded shadow">
<p className="text-sm">Ventas</p>
<p className="text-xl font-bold">
${metrics.kpis.totalSales.toFixed(2)}
</p>
</div>

<div className="bg-white p-4 rounded shadow">
<p className="text-sm">Ticket Promedio</p>
<p className="text-xl font-bold">
${metrics.kpis.ticketAverage.toFixed(2)}
</p>
</div>

<div className="bg-white p-4 rounded shadow">
<p className="text-sm">CMV</p>
<p className="text-xl font-bold">
${metrics.kpis.cmv.toFixed(2)}
</p>
</div>

<div className="bg-white p-4 rounded shadow">
<p className="text-sm">Margen</p>
<p className="text-xl font-bold">
${metrics.kpis.margin.toFixed(2)}
</p>
</div>

<div className="bg-white p-4 rounded shadow">
<p className="text-sm">Productos vendidos</p>
<p className="text-xl font-bold">
{metrics.kpis.itemsSold}
</p>
</div>

</div>

{/* CHART VIEW */}

{viewMode==="charts" && (

<>

<div className="grid md:grid-cols-2 gap-6">

<div className="bg-white p-4 rounded shadow">

<h3 className="mb-3 font-semibold">
Ventas
</h3>

<ResponsiveContainer width="100%" height={300}>

<BarChart data={metrics.salesChart}>

<CartesianGrid strokeDasharray="3 3"/>

<XAxis dataKey="date"/>

<YAxis/>

<Tooltip/>

<Bar dataKey="total" fill="#58735A"/>

</BarChart>

</ResponsiveContainer>

</div>


<div className="bg-white p-4 rounded shadow">

<h3 className="mb-3 font-semibold">
Métodos de pago
</h3>

<ResponsiveContainer width="100%" height={300}>

<PieChart>

<Pie
data={metrics.paymentMethods}
dataKey="value"
nameKey="name"
outerRadius={100}
>

{metrics.paymentMethods.map((entry,index)=>(
<Cell
key={index}
fill={COLORS[index%COLORS.length]}
/>
))}

</Pie>

<Tooltip/>

<Legend/>

</PieChart>

</ResponsiveContainer>

</div>

</div>


<div className="grid md:grid-cols-2 gap-6 mt-6">

<div className="bg-white p-4 rounded shadow">

<h3 className="mb-3 font-semibold">
CMV vs Margen
</h3>

<ResponsiveContainer width="100%" height={300}>

<PieChart>

<Pie
data={metrics.cmvMargin}
dataKey="value"
nameKey="name"
outerRadius={100}
>

{metrics.cmvMargin.map((entry,index)=>(
<Cell
key={index}
fill={COLORS[index%COLORS.length]}
/>
))}

</Pie>

<Tooltip/>

<Legend/>

</PieChart>

</ResponsiveContainer>

</div>


<div className="bg-white p-4 rounded shadow">

<h3 className="mb-3 font-semibold">
Top Productos
</h3>

<ResponsiveContainer width="100%" height={300}>

<BarChart data={metrics.topProducts}>

<CartesianGrid strokeDasharray="3 3"/>

<XAxis dataKey="name"/>

<YAxis/>

<Tooltip/>

<Bar dataKey="quantity" fill="#3F1907"/>

</BarChart>

</ResponsiveContainer>

</div>

</div>

</>

)}


{/* DATA VIEW */}

{viewMode==="data" && (

<div className="grid gap-6">

<div className="bg-white p-4 rounded shadow">

<h3 className="mb-3 font-semibold">
Ventas por día
</h3>

<table className="w-full">

<thead>

<tr>
<th>Fecha</th>
<th>Total</th>
</tr>

</thead>

<tbody>

{metrics.salesChart.map((s,i)=>(
<tr key={i}>
<td>{s.date}</td>
<td>${s.total.toFixed(2)}</td>
</tr>
))}

</tbody>

</table>

</div>


<div className="bg-white p-4 rounded shadow">

<h3 className="mb-3 font-semibold">
Métodos de pago
</h3>

<table className="w-full">

<thead>

<tr>
<th>Método</th>
<th>Total</th>
</tr>

</thead>

<tbody>

{metrics.paymentMethods.map((p,i)=>(
<tr key={i}>
<td>{p.name}</td>
<td>${p.value.toFixed(2)}</td>
</tr>
))}

</tbody>

</table>

</div>


<div className="bg-white p-4 rounded shadow">

<h3 className="mb-3 font-semibold">
Top productos
</h3>

<table className="w-full">

<thead>

<tr>
<th>Producto</th>
<th>Ventas</th>
</tr>

</thead>

<tbody>

{metrics.topProducts.map((p,i)=>(
<tr key={i}>
<td>{p.name}</td>
<td>{p.quantity}</td>
</tr>
))}

</tbody>

</table>

</div>

</div>

)}


{/* INVENTORY ALERT */}

<div className="bg-white p-4 rounded shadow mt-6">

<h3 className="font-semibold mb-3">
Alertas de inventario
</h3>

{metrics.lowStock.length===0 ?(

<p className="text-green-600">
No hay productos con bajo stock
</p>

):(

<ul>

{metrics.lowStock.map((item,i)=>(
<li key={i} className="text-red-600">
{item.name} — {item.current_quantity} {item.unit}
</li>
))}

</ul>

)}

</div>


</div>

);

};

export default DashboardPage;